import { supabase } from './supabase';
import type {
  Outlet,
  StaffProfile,
  StaffLeave,
  StaffAssignment,
  StaffScheduleView,
  StaffWithProfile,
  DailySchedule,
  OutletCoverage,
  SchedulingConstraints,
  LeaveStatus,
  SchedulePublicationInfo,
  ScheduleViewer
} from '../types/scheduling';

// ==================== HELPERS ====================

// Helper to get outlet name (handles both 'name' and 'outletname' columns)
export function getOutletName(outlet: Outlet): string {
  return outlet.name || outlet.outletname || 'Unknown Outlet';
}

// ==================== OUTLETS ====================

export async function getOutlets(includeInactive = false) {
  let query = supabase
    .from('outlets')
    .select('*')
    .order('name');

  if (!includeInactive) {
    query = query.eq('is_active', true);
  }

  const { data, error } = await query;
  if (error) throw error;
  return data as Outlet[];
}

export async function getOutlet(id: number) {
  const { data, error } = await supabase
    .from('outlets')
    .select('*')
    .eq('id', id)
    .single();

  if (error) throw error;
  return data as Outlet;
}

export async function createOutlet(outlet: Omit<Outlet, 'id' | 'created_at' | 'updated_at'>) {
  // Set both name and outletname for compatibility
  const outletData = {
    ...outlet,
    name: outlet.name || outlet.outletname,
    outletname: outlet.outletname || outlet.name
  };

  const { data, error } = await supabase
    .from('outlets')
    .insert(outletData)
    .select()
    .single();

  if (error) throw error;
  return data as Outlet;
}

export async function updateOutlet(id: number, updates: Partial<Outlet>) {
  // Sync name and outletname if either is updated
  const outletUpdates = { ...updates };
  if (updates.name) outletUpdates.outletname = updates.name;
  if (updates.outletname) outletUpdates.name = updates.outletname;

  const { data, error } = await supabase
    .from('outlets')
    .update(outletUpdates)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as Outlet;
}

export async function toggleOutlet(id: number, isActive: boolean) {
  const { data, error } = await supabase
    .from('outlets')
    .update({ is_active: isActive })
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as Outlet;
}

// Legacy function names for backward compatibility
export const getBranches = getOutlets;
export const getBranch = getOutlet;
export const createBranch = createOutlet;
export const updateBranch = updateOutlet;
export const deleteBranch = (id: number) => toggleOutlet(id, false);

// ==================== STAFF PROFILES ====================

export async function getStaffProfiles() {
  // First get staff user IDs from user_roles
  const { data: staffRoles, error: rolesError } = await supabase
    .from('user_roles')
    .select('user_id')
    .eq('role', 'staff');

  if (rolesError) throw rolesError;
  if (!staffRoles || staffRoles.length === 0) return [];

  const staffUserIds = staffRoles.map(r => r.user_id);

  // Get staff profiles, leaderboard points, and user profiles separately
  const [profilesResult, userProfilesResult, leaderboardResult, outletsResult] = await Promise.all([
    supabase
      .from('staff_profiles')
      .select('*')
      .in('user_id', staffUserIds)
      .order('is_active', { ascending: false }),
    supabase
      .from('user_profiles')
      .select('user_id, display_name')
      .in('user_id', staffUserIds),
    supabase
      .from('leaderboard_points')
      .select('user_id, points')
      .in('user_id', staffUserIds),
    supabase
      .from('outlets')
      .select('*')
  ]);

  if (profilesResult.error) throw profilesResult.error;
  if (!profilesResult.data) return [];

  // Create lookup maps
  const userProfilesMap = new Map(
    (userProfilesResult.data || []).map(up => [up.user_id, up.display_name])
  );
  const leaderboardMap = new Map(
    (leaderboardResult.data || []).map(lp => [lp.user_id, lp.points])
  );
  const outletsMap = new Map(
    (outletsResult.data || []).map(o => [o.id, o])
  );

  // Combine the data
  return profilesResult.data.map(profile => ({
    user_id: profile.user_id,
    display_name: userProfilesMap.get(profile.user_id) || 'Unknown',
    profile: profile as StaffProfile,
    skill_score: leaderboardMap.get(profile.user_id) || 0,
    home_outlet: profile.home_outlet_id ? outletsMap.get(profile.home_outlet_id) : undefined,
    secondary_outlet: profile.secondary_outlet_id ? outletsMap.get(profile.secondary_outlet_id) : undefined
  })) as StaffWithProfile[];
}

export async function getStaffProfile(userId: string) {
  const { data, error } = await supabase
    .from('staff_profiles')
    .select('*')
    .eq('user_id', userId)
    .single();

  if (error) throw error;
  return data as StaffProfile;
}

export async function createStaffProfile(profile: Omit<StaffProfile, 'id' | 'created_at' | 'updated_at'>) {
  const { data, error } = await supabase
    .from('staff_profiles')
    .insert(profile)
    .select()
    .single();

  if (error) throw error;
  return data as StaffProfile;
}

export async function updateStaffProfile(userId: string, updates: Partial<StaffProfile>) {
  const { data, error } = await supabase
    .from('staff_profiles')
    .update(updates)
    .eq('user_id', userId)
    .select()
    .single();

  if (error) throw error;
  return data as StaffProfile;
}

// ==================== LEAVES ====================

export async function getLeaves(
  startDate?: string,
  endDate?: string,
  options: { status?: LeaveStatus | 'all'; userId?: string } = {}
) {
  const { status, userId } = options;

  let query = supabase
    .from('staff_leaves')
    .select('*')
    .order('leave_date', { ascending: true });

  if (status && status !== 'all') {
    query = query.eq('status', status);
  } else if (!status) {
    query = query.eq('status', 'approved');
  }

  if (startDate) {
    query = query.gte('leave_date', startDate);
  }
  if (endDate) {
    query = query.lte('leave_date', endDate);
  }
  if (userId) {
    query = query.eq('user_id', userId);
  }

  const { data, error } = await query;
  if (error) throw error;
  return data as StaffLeave[];
}

export async function getLeavesByDate(date: string) {
  const { data, error } = await supabase
    .from('staff_leaves')
    .select('*')
    .eq('leave_date', date)
    .eq('status', 'approved');

  if (error) throw error;
  return data as StaffLeave[];
}

export async function createLeave(leave: Omit<StaffLeave, 'id' | 'created_at' | 'updated_at'>) {
  const { data, error } = await supabase
    .from('staff_leaves')
    .insert(leave)
    .select()
    .single();

  if (error) throw error;
  return data as StaffLeave;
}

export async function updateLeave(id: string, updates: Partial<StaffLeave>) {
  const { data, error } = await supabase
    .from('staff_leaves')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as StaffLeave;
}

export async function deleteLeave(id: string) {
  const { error } = await supabase
    .from('staff_leaves')
    .delete()
    .eq('id', id);

  if (error) throw error;
}

// ==================== ASSIGNMENTS ====================

export async function getAssignmentsByDate(date: string) {
  const { data, error } = await supabase
    .from('staff_schedule_view')
    .select('*')
    .eq('assignment_date', date)
    .order('outlet_name');

  if (error) throw error;
  return data as StaffScheduleView[];
}

export async function getAssignments(startDate: string, endDate: string) {
  const { data, error } = await supabase
    .from('staff_schedule_view')
    .select('*')
    .gte('assignment_date', startDate)
    .lte('assignment_date', endDate)
    .order('assignment_date', { ascending: true })
    .order('outlet_name');

  if (error) throw error;
  return data as StaffScheduleView[];
}

export async function createAssignment(assignment: Omit<StaffAssignment, 'id' | 'created_at' | 'updated_at'>) {
  const { data, error } = await supabase
    .from('staff_assignments')
    .insert(assignment)
    .select()
    .single();

  if (error) throw error;
  return data as StaffAssignment;
}

export async function updateAssignment(id: string, updates: Partial<StaffAssignment>) {
  const { data, error } = await supabase
    .from('staff_assignments')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as StaffAssignment;
}

export async function deleteAssignment(id: string) {
  const { error } = await supabase
    .from('staff_assignments')
    .delete()
    .eq('id', id);

  if (error) throw error;
}

export async function deleteAssignmentsByDate(date: string) {
  const { error } = await supabase
    .from('staff_assignments')
    .delete()
    .eq('assignment_date', date);

  if (error) throw error;
}

// ==================== SCHEDULING LOGIC ====================

export async function getDailySchedule(date: string, isAdmin: boolean = true): Promise<DailySchedule> {
  // For non-admin users, check if schedule is published
  if (!isAdmin) {
    const published = await isSchedulePublished(date);
    if (!published) {
      // Return empty schedule if not published
      return {
        date,
        assignments: [],
        unassigned_staff: [],
        outlet_coverage: []
      };
    }
  }

  const [assignments, allStaff, outlets, leaves] = await Promise.all([
    getAssignmentsByDate(date),
    getStaffProfiles(),
    getOutlets(true),
    getLeavesByDate(date)
  ]);

  const assignedUserIds = new Set(assignments.map(a => a.user_id));
  const onLeaveUserIds = new Set(leaves.map(l => l.user_id));

  // Only show active staff who are not assigned and not on leave
  const unassigned_staff = allStaff.filter(
    staff => !assignedUserIds.has(staff.user_id) &&
      !onLeaveUserIds.has(staff.user_id) &&
      staff.profile.is_active
  );

  const outlet_coverage: OutletCoverage[] = outlets.map(outlet => {
    const assigned_staff = assignments.filter(a => a.outlet_id === outlet.id);
    return {
      outlet_id: outlet.id,
      outlet_name: getOutletName(outlet),
      staff_required: outlet.staff_required,
      staff_assigned: assigned_staff.length,
      is_complete: assigned_staff.length >= outlet.staff_required,
      traffic_level: outlet.traffic_level,
      assigned_staff,
      is_active: outlet.is_active
    };
  });

  return {
    date,
    assignments,
    unassigned_staff,
    outlet_coverage
  };
}

export async function generateSchedule(date: string): Promise<StaffAssignment[]> {
  // Delete existing assignments for the date (if regenerating)
  await deleteAssignmentsByDate(date);

  // Get constraints (only staff with role='staff')
  const [allStaff, outlets, leaves] = await Promise.all([
    getStaffProfiles(),
    getOutlets(),
    getLeavesByDate(date)
  ]);

  const onLeaveUserIds = new Set(leaves.map(l => l.user_id));
  // Only include active staff (not on leave, not marked inactive)
  const availableStaff = allStaff.filter(
    s => !onLeaveUserIds.has(s.user_id) && s.profile.is_active
  );

  // Get all previous assignments to track which outlets staff have been assigned to
  const { data: previousAssignments, error: assignmentsError } = await supabase
    .from('staff_assignments')
    .select('user_id, outlet_id')
    .neq('assignment_date', date); // Exclude today's date

  if (assignmentsError) {
    console.warn('Could not load previous assignments:', assignmentsError);
  }

  // Build a map of staff_id -> Set of outlet_ids they've been assigned to
  const staffOutletHistory = new Map<string, Set<number>>();
  (previousAssignments || []).forEach(assignment => {
    if (!staffOutletHistory.has(assignment.user_id)) {
      staffOutletHistory.set(assignment.user_id, new Set());
    }
    staffOutletHistory.get(assignment.user_id)!.add(assignment.outlet_id);
  });

  // Sort outlets: high traffic first
  const sortedOutlets = [...outlets].sort((a, b) => {
    const trafficOrder = { high: 3, medium: 2, low: 1 };
    return trafficOrder[b.traffic_level] - trafficOrder[a.traffic_level];
  });

  const assignments: StaffAssignment[] = [];
  const assignedStaffIds = new Set<string>();

  // Step 1: Assign fixed staff to their home outlets
  for (const outlet of sortedOutlets) {
    const fixedStaff = availableStaff.filter(
      s => s.profile.is_fixed &&
        s.profile.home_outlet_id === outlet.id &&
        !assignedStaffIds.has(s.user_id)
    );

    for (const staff of fixedStaff) {
      if (assignments.filter(a => a.outlet_id === outlet.id).length < outlet.staff_required) {
        assignments.push({
          user_id: staff.user_id,
          outlet_id: outlet.id,
          assignment_date: date,
          is_manual: false
        } as StaffAssignment);
        assignedStaffIds.add(staff.user_id);
      }
    }
  }

  // Step 2: Fill remaining slots
  for (const outlet of sortedOutlets) {
    const currentAssigned = assignments.filter(a => a.outlet_id === outlet.id).length;
    const slotsNeeded = outlet.staff_required - currentAssigned;

    if (slotsNeeded > 0) {
      // Get available rotating staff, excluding those already assigned to this outlet
      // UNLESS it's their home or secondary outlet
      let candidateStaff = availableStaff.filter(s => {
        // Check if already assigned today
        if (assignedStaffIds.has(s.user_id)) {
          return false;
        }

        // Check if this outlet is their home or secondary outlet
        const isHomeOutlet = s.profile.home_outlet_id === outlet.id;
        const isSecondaryOutlet = s.profile.secondary_outlet_id === outlet.id;

        // Check rotation rules
        if (!s.profile.can_rotate) {
          // If cannot rotate, only allow if it's their home or secondary outlet
          if (!isHomeOutlet && !isSecondaryOutlet) {
            return false;
          }
        }

        // If it's home or secondary, always allow
        if (isHomeOutlet || isSecondaryOutlet) {
          return true;
        }

        // Otherwise, check if they've been assigned to this outlet before
        const assignedOutlets = staffOutletHistory.get(s.user_id);
        // if (assignedOutlets && assignedOutlets.has(outlet.id)) {
        //   return false; // Already assigned to this outlet before
        // }

        return true;
      });

      // Prioritize staff by leaderboard points (higher points = higher priority)
      // This applies to all outlets, not just high traffic
      candidateStaff = candidateStaff.sort((a, b) => {
        // First priority: leaderboard points (descending)
        const pointsDiff = b.skill_score - a.skill_score;
        if (pointsDiff !== 0) return pointsDiff;

        // Second priority: home/secondary outlet preference
        const aIsPreferred = (a.profile.home_outlet_id === outlet.id || a.profile.secondary_outlet_id === outlet.id) ? 1 : 0;
        const bIsPreferred = (b.profile.home_outlet_id === outlet.id || b.profile.secondary_outlet_id === outlet.id) ? 1 : 0;
        return bIsPreferred - aIsPreferred;
      });

      // Assign staff to fill slots
      for (let i = 0; i < Math.min(slotsNeeded, candidateStaff.length); i++) {
        const staff = candidateStaff[i];
        assignments.push({
          user_id: staff.user_id,
          outlet_id: outlet.id,
          assignment_date: date,
          is_manual: false
        } as StaffAssignment);
        assignedStaffIds.add(staff.user_id);
      }
    }
  }

  // Save all assignments to database
  if (assignments.length > 0) {
    const { data, error } = await supabase
      .from('staff_assignments')
      .insert(assignments)
      .select();

    if (error) throw error;
    return data as StaffAssignment[];
  }

  return [];
}

// ==================== SCHEDULE PUBLICATIONS ====================

export async function isSchedulePublished(date: string): Promise<boolean> {
  const { data, error } = await supabase.rpc('is_schedule_published', {
    p_date: date
  });

  if (error) throw error;
  return data as boolean;
}

export async function getSchedulePublicationInfo(date: string): Promise<SchedulePublicationInfo | null> {
  const { data, error } = await supabase.rpc('get_schedule_publication_info', {
    p_date: date
  });

  if (error) throw error;
  if (!data || data.length === 0) return null;
  return data[0] as SchedulePublicationInfo;
}

export async function publishSchedule(date: string): Promise<{ publicationId: string; isRepublish: boolean }> {
  // Get current user
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('User not authenticated');

  // Get current assignments before publishing
  const oldAssignments = await getAssignmentsByDate(date);

  // Check if already published
  const existingPublication = await getSchedulePublicationInfo(date);
  const isRepublish = existingPublication !== null;

  // Publish the schedule
  const { data: publicationId, error: publishError } = await supabase.rpc('publish_schedule', {
    p_publication_date: date,
    p_published_by: user.id
  });

  if (publishError) throw publishError;

  // Get current assignments after publishing
  const newAssignments = await getAssignmentsByDate(date);

  // If this is a republish, notify affected users
  if (isRepublish) {
    const oldAssignmentsJson = oldAssignments.map(a => ({
      user_id: a.user_id,
      outlet_id: a.outlet_id
    }));

    const newAssignmentsJson = newAssignments.map(a => ({
      user_id: a.user_id,
      outlet_id: a.outlet_id
    }));

    const { error: notifyError } = await supabase.rpc('notify_schedule_changes', {
      p_publication_date: date,
      p_old_assignments: oldAssignmentsJson,
      p_new_assignments: newAssignmentsJson
    });

    if (notifyError) {
      console.error('Failed to send notifications:', notifyError);
    }
  } else {
    // First time publish - notify all assigned staff
    for (const assignment of newAssignments) {
      const { error: notifyError } = await supabase.rpc('notify_staff_of_assignment', {
        p_user_id: assignment.user_id,
        p_outlet_name: assignment.outlet_name,
        p_assignment_date: date,
        p_is_revision: false
      });

      if (notifyError) {
        console.error('Failed to send notification:', notifyError);
      }
    }
  }

  return { publicationId: publicationId as string, isRepublish };
}

// ==================== SCHEDULE VIEWS ====================

export async function logScheduleView(date: string) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return;

  const published = await isSchedulePublished(date);
  if (!published) return;

  const { error } = await supabase
    .from('schedule_views')
    .upsert({
      schedule_date: date,
      user_id: user.id,
      viewed_at: new Date().toISOString()
    }, {
      onConflict: 'schedule_date,user_id'
    });

  if (error) {
    console.error('Failed to log schedule view', error);
  }
}

export async function getScheduleViewers(date: string): Promise<ScheduleViewer[]> {
  const { data, error } = await supabase
    .from('schedule_views')
    .select('user_id, viewed_at')
    .eq('schedule_date', date)
    .order('viewed_at', { ascending: false });

  if (error) throw error;
  if (!data || data.length === 0) return [];

  const userIds = Array.from(new Set(data.map(view => view.user_id)));

  const [profilesResult, photosResult] = await Promise.all([
    supabase
      .from('user_profiles')
      .select('user_id, display_name')
      .in('user_id', userIds),
    supabase
      .from('profile_photos')
      .select('user_id, photo_url')
      .in('user_id', userIds)
  ]);

  if (profilesResult.error) throw profilesResult.error;
  if (photosResult.error) throw photosResult.error;

  const profileMap = new Map(
    (profilesResult.data || []).map(profile => [profile.user_id, profile.display_name])
  );
  const photoMap = new Map(
    (photosResult.data || []).map(photo => [photo.user_id, photo.photo_url])
  );

  return data.map(view => ({
    user_id: view.user_id,
    display_name: profileMap.get(view.user_id) || 'Unknown Staff',
    photo_url: photoMap.get(view.user_id) || null,
    viewed_at: view.viewed_at
  }));
}
